import { ReactElement, useState, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';
import {
  Drawer,
  Stack,
  Typography,
  Paper,
  IconButton,
  Tabs,
  Divider,
  Tab,
} from '@mui/material';
import { Menu } from '@/types/menus';
import useSnackbar from '@/hooks/useSnackbar';
import { X } from 'lucide-react';
import { useUpdateMenuMutation } from '@/features/organizations/api/menusApi';
import MenuDetailsTab, { MenuDetailsSchema } from './MenuDetailsTab';
import MenuStructureTab, { MenuStructureSchema } from './MenuStructureTab';
import UpdateMenuActions from './UpdateMenuActions';
import { useAccessControl } from '@/features/access-control';

type Tab = {
  label: string;
  component: ReactElement;
  schema?: any;
};

interface Props {
  selectedMenu?: Menu;
  onClose: () => void;
}

const UpdateMenuDrawer = (props: Props) => {
  const { selectedMenu, onClose } = props;
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const [updateMenu, updateMenuMutation] = useUpdateMenuMutation();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [activeTab, setActiveTab] = useState('details');

  const isNewContent = useMemo(() => !selectedMenu?.id, [selectedMenu]);

  const tabs = useMemo(() => {
    let draft: Record<string, Tab> = {
      details: {
        label: 'Detalles',
        component: <MenuDetailsTab />,
        schema: MenuDetailsSchema,
      },
    };

    if (hasAccess('view.menu.structure')) {
      draft = {
        ...draft,
        structure: {
          label: 'Diseño',
          component: <MenuStructureTab />,
          schema: MenuStructureSchema,
        },
      };
    }

    return draft;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async (values: Menu) => {
    const response = await updateMenu({ menu: values, organizationId });

    if (response?.error) {
      openSnackbar('Error al actualizar el menú', 'error');
    } else {
      openSnackbar('Menú actualizado', 'success');
      onClose();
    }
  };

  return (
    <Drawer open={Boolean(selectedMenu && selectedMenu)} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: 2,
          paddingBottom: 0,
        }}
      >
        <Typography variant="h2">
          {isNewContent ? 'Crear' : 'Editar'} menú
        </Typography>
        <Paper
          sx={{
            borderRadius: '50%',
          }}
        >
          <IconButton onClick={onClose} color="primary">
            <X />
          </IconButton>
        </Paper>
      </Stack>

      <Tabs value={activeTab} onChange={handleChange} sx={{ paddingX: 2 }}>
        {Object.entries(tabs).map(([tabKey, tab]) =>
          !isNewContent || tabKey !== 'settings' ? (
            <Tab key={tabKey} label={tab.label} value={tabKey} />
          ) : null
        )}
      </Tabs>

      <Divider />

      <Stack padding={2} gap={2} flex={1}>
        <Formik
          initialValues={{ ...selectedMenu }}
          onSubmit={handleSubmit}
          validationSchema={tabs[activeTab].schema}
        >
          {() => {
            return (
              <Form>
                <Stack gap={2}>
                  {tabs[activeTab].component}
                  <UpdateMenuActions
                    {...props}
                    isLoading={updateMenuMutation.isLoading}
                  />
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Stack>
    </Drawer>
  );
};

export default UpdateMenuDrawer;
